import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, MessageSquare, AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

type RequestType = 'feedback' | 'issue';
type View = 'menu' | 'form';

interface HelpSupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portalType: 'partner' | 'monument';
  companyId?: string;
  companyName?: string;
}

const HelpSupportDialog = ({ 
  open, 
  onOpenChange, 
  portalType,
  companyId,
  companyName 
}: HelpSupportDialogProps) => {
  const { user } = useAuth();
  const [view, setView] = useState<View>('menu');
  const [requestType, setRequestType] = useState<RequestType>('feedback');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenForm = (type: RequestType) => {
    setRequestType(type);
    setView('form');
  };

  const handleBack = () => {
    setView('menu');
    setSubject('');
    setMessage('');
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setView('menu');
      setSubject('');
      setMessage('');
    }, 200);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (!user) {
      toast.error('You must be logged in to submit a request');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('support_requests')
        .insert({
          user_id: user.id,
          portal_type: portalType,
          company_id: companyId,
          company_name: companyName,
          request_type: requestType,
          subject: subject.trim() || null,
          message: message.trim(),
        });

      if (error) throw error;

      toast.success(
        requestType === 'feedback' 
          ? 'Thank you for your feedback! We appreciate it.' 
          : 'Issue reported! We\'ll look into it soon.'
      );
      handleClose();
    } catch (error) {
      console.error('Error submitting support request:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const supportEmail = 'support@pontis.life';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {view === 'menu' ? 'Help & Support' : (
              requestType === 'feedback' ? 'Share Feedback' : 'Report an Issue'
            )}
          </DialogTitle>
          <DialogDescription>
            {view === 'menu' 
              ? 'How can we help you today?' 
              : requestType === 'feedback'
                ? 'Your feedback helps us improve the platform.'
                : 'Describe the issue you\'re experiencing.'
            }
          </DialogDescription>
        </DialogHeader>

        {view === 'menu' ? (
          <div className="space-y-3 py-2">
            {/* Contact Support */}
            <a 
              href={`mailto:${supportEmail}?subject=Support Request - ${portalType === 'partner' ? 'Partner Portal' : 'Monument Company Portal'}`}
              className="flex items-center gap-3 p-4 rounded-lg border hover:bg-muted transition-colors"
            >
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Contact Support</p>
                <p className="text-sm text-muted-foreground">Email us directly for help</p>
              </div>
            </a>

            {/* Share Feedback */}
            <button
              onClick={() => handleOpenForm('feedback')}
              className="w-full flex items-center gap-3 p-4 rounded-lg border hover:bg-muted transition-colors text-left"
            >
              <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Share Feedback</p>
                <p className="text-sm text-muted-foreground">Suggest improvements or features</p>
              </div>
            </button>

            {/* Report Issue */}
            <button
              onClick={() => handleOpenForm('issue')}
              className="w-full flex items-center gap-3 p-4 rounded-lg border hover:bg-muted transition-colors text-left"
            >
              <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Report an Issue</p>
                <p className="text-sm text-muted-foreground">Let us know if something isn't working</p>
              </div>
            </button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject (optional)</Label>
              <Input
                id="subject"
                placeholder="Brief summary..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message <span className="required-asterisk">*</span></Label>
              <Textarea
                id="message"
                placeholder={
                  requestType === 'feedback'
                    ? "What would make the platform better for you?"
                    : "Describe what happened and what you expected..."
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !message.trim()}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HelpSupportDialog;
